package ai.jessie.web.projects

import ai.jessie.shared.projects.ProjectResponse
import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.settings.FocusReturn
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.linkButton
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

// UC-010 management for owners and standard members. `projectId` is null on the list page. The
// project form and its review open as a slide-over above the list or the project
// (`docs/design/screens/13-PositionSlideOver.png`); the header actions live in the page.
@Composable
fun projectRecords(
    organisationId: String,
    projectId: String?,
    state: ProjectUiState,
    actions: ProjectActions,
) {
    val focusReturn = remember { FocusReturn() }
    projectFocus(state, focusReturn)
    val editing = state.draft != null
    if (!editing) projectMessages(state)
    if (state.error?.authenticationRequired != true)
        projectBody(organisationId, projectId, state, actions)
    if (editing) projectEditor(state, actions)
    if (projectId != null)
        P {
            A(href = "/organisations/$organisationId/projects") { Text("Zurück zur Projektliste") }
        }
    P { A(href = "/organisations/$organisationId") { Text("Zurück zur Organisation") } }
}

// While the slide-over is open it shows these itself.
@Composable
private fun projectMessages(state: ProjectUiState) {
    state.error?.let { Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(it, state.busy) } }
    if (state.busy) projectStatus("Wird geladen oder gespeichert …")
    state.notice?.let { projectStatus(it) }
}

@Composable
private fun projectBody(
    organisationId: String,
    projectId: String?,
    state: ProjectUiState,
    actions: ProjectActions,
) {
    val current = state.current
    when {
        state.change != null -> projectChangeConfirmation(state, actions)
        current != null -> {
            projectRecord(current, state, actions.takeIf { projectId != null })
            if (projectId == null) savedActions(organisationId, current, state, actions)
            else
                Div(attrs = RowStyle.toAttrs()) {
                    themedButton(
                        "Projekt erneut laden",
                        actions.refresh,
                        variant = ButtonVariant.GHOST,
                        disabled = state.busy,
                    )
                }
        }
        projectId == null -> projectList(organisationId, state, actions)
        // A9: a removed project offers nothing further here.
        !state.removed ->
            Div(attrs = RowStyle.toAttrs()) {
                themedButton("Projekt erneut laden", actions.refresh, disabled = state.busy)
            }
    }
}

// Every view change replaces the control that had focus, so the page title takes it again; while
// a problem is shown, the alert or the marked field keeps focus, and an open form or confirmation
// focuses itself. The first render leaves the frame's own title focus alone.
@Composable
private fun projectFocus(state: ProjectUiState, focusReturn: FocusReturn) {
    LaunchedEffect(state.draft == null, state.change == null, state.current == null) {
        val settled = state.draft == null && state.change == null && state.error == null
        if (focusReturn.pending && settled && !AppGlobals.isExporting)
            (document.getElementById("page-title") as? HTMLElement)?.focus()
        focusReturn.pending = true
    }
}

@Composable
internal fun projectStatus(text: String) {
    P(attrs = StatusStyle.toAttrs { attr("role", "status") }) { Text(text) }
}

@Composable
private fun savedActions(
    organisationId: String,
    current: ProjectResponse,
    state: ProjectUiState,
    actions: ProjectActions,
) {
    Div(attrs = RowStyle.toAttrs()) {
        linkButton(
            "Projekt öffnen",
            "/organisations/$organisationId/projects/${current.id}",
            variant = ButtonVariant.PRIMARY,
        )
        themedButton("Zur Projektliste", actions.close, disabled = state.busy)
    }
}

/** The page-header actions of the detail page (`05-Projekt.png`) for the returned project. */
@Composable
fun projectHeaderActions(current: ProjectResponse, busy: Boolean, actions: ProjectActions) {
    themedButton("Projekt bearbeiten", actions.edit, disabled = busy)
    if (current.active)
        themedButton(
            "Projekt deaktivieren",
            { actions.deactivate(null) },
            disabled = busy,
            elementId = deactivateId(null),
        )
    themedButton("Projekt entfernen", { actions.remove(null) }, disabled = busy)
}

internal const val PROJECT_TITLE_ID = "project-title"

/** Title of the list page and parent of every project breadcrumb. */
const val PROJECTS_TITLE = "Projekte"
